/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	BP,
	CompileContext,
	createEmptyScope,
	createRenderFunctionFromModel,
	DESIGN_CONTEXT_READ_MODE,
	DesignContext,
	ICompileResult,
	ISchemaConstObjectModel,
	ISchemaFlowNodeListModel,
	ISchemaFlowNodeTypeDefinitionMap,
	RUNTIME_CONTEXT_MODE,
	RuntimeContext,
	SCHEMA_CONST_ANY_VALUE_TYPE,
	serializeIDTToYAML,
	TGetBlueprintSchemaModel,
	TGetBlueprintSchemaSpec,
	TSchemaConstObjectProps
} from "@adapptio/lib-blueprint";
import {
	BlueprintEndpoint,
	createViewScope,
	IActionDebugData,
	TBlueprintEndpointSchema,
	TBlueprintViewSchema
} from "@adapptio/lib-core";
import { debounce } from "@adapptio/lib-editor-ui";
import {
	createEventEmitter,
	emitEvent,
	offEvent,
	onEvent,
	removeAllEventListeners,
	UndoRedoManager
} from "@adapptio/lib-shared";
import { IntegrationService } from "../../../integration/IntegrationService";
import { EditorRenderContext } from "../../../runtime/EditorRenderContext";
import { EditorRoutingManager } from "../../../runtime/EditorRoutingManager";
import { RuntimeService } from "../../../runtime/RuntimeService";
import { IErrorListGroup } from "../../../ui/components/ErrorList/ErrorListTypes";
import { EndpointEditorEditSelection } from "./EndpointEditorEditSelection";

export interface IEndpointModelOpts {}

export enum ENDPOINT_MODEL_MODE {
	EDIT,
	PREVIEW,
	PREVIEW_EDIT
}

const paramsSchema = BP.Const.Object({
	props: {
		body: BP.Prop(
			BP.Const.Any({
				label: "Body",
				description: "Body of a request.",
				defaultType: SCHEMA_CONST_ANY_VALUE_TYPE.STRING
			})
		),
		headers: BP.Prop(
			BP.Const.Array({
				label: "Headers",
				description: "Headers of a request.",
				items: BP.Const.Object({
					props: {
						name: BP.Prop(
							BP.Const.String({
								label: "Name",
								placeholder: "X-Header-Name"
							})
						),
						value: BP.Prop(
							BP.Const.String({
								label: "Value"
							})
						)
					}
				})
			})
		),
		query: BP.Prop(
			BP.Const.Any({
				label: "Query",
				description:
					"Url query string.\nIf the query is defined as a string, it will be send 1:1. Otherwise it will be serialized.",
				defaultType: SCHEMA_CONST_ANY_VALUE_TYPE.MAP,
				childDefaultType: SCHEMA_CONST_ANY_VALUE_TYPE.STRING,
				constOnly: true
			})
		),
		params: BP.Prop(
			BP.Const.Map({
				label: "Route Params",
				description: "Params injected as route params (from path).",
				value: BP.Const.String({})
			})
		)
	}
});

/**
 * Endpoint Document Model
 */
export class EndpointModel {
	/* Contexts */
	private renderCtx: EditorRenderContext<TBlueprintViewSchema>;
	private dCtx: DesignContext;
	private rCtx: RuntimeContext;

	/** Selection */
	private selection: EndpointEditorEditSelection;

	/** Emitted when parse errors has changed */
	public onParseErrors = createEventEmitter<void>();

	/** Emitted when runtime context has rendered */
	public onRender = createEventEmitter<void>();

	/** Emitted when blueprint model has changed */
	public onModelChange = createEventEmitter<void>();

	/** Routing manager */
	private routingManager: EditorRoutingManager;

	/** Blueprint model */
	private model: TGetBlueprintSchemaModel<TBlueprintEndpointSchema> = null;

	/** If render was requested */
	private renderRequested = false;

	/** Emitted when runtime errors has changed */
	public onRuntimeErrors = createEventEmitter<void>();

	/** Rendered model spec */
	private modelSpec: TGetBlueprintSchemaSpec<TBlueprintViewSchema> = null;

	/** Endpoint params model */
	private paramsModel: ISchemaConstObjectModel<TSchemaConstObjectProps> | null = null;

	/** Emitted when new params model has been built */
	public onParamsModelBuilt = createEventEmitter<void>();

	/** Emitted when params model has changed */
	public onParamsModelChange = createEventEmitter<void>();

	/** Undo/Redo manager */
	public undoRedoManager = new UndoRedoManager({ historyLimit: 10 });
	private lastSnapshot = null;

	/* Resource event handlers */
	private handleGlobalsContainerChange: () => void;

	/**
	 * Document constructor
	 */
	public constructor(
		private runtimeSvc: RuntimeService,
		private integrationSvc: IntegrationService,
		private opts: IEndpointModelOpts
	) {
		this.routingManager = runtimeSvc.createRoutingManager();
		this.selection = new EndpointEditorEditSelection();

		const runtimeResolvers = runtimeSvc.getBaseResolvers();
		const integrationResolvers = integrationSvc.getBaseResolvers();

		this.renderCtx = runtimeSvc.createRenderContext({
			mode: RUNTIME_CONTEXT_MODE.EDITOR,
			mediaResolution: "screenBase",
			routingManager: this.routingManager,
			getComponentListSpec: (spec) => spec?.spec?.content,
			eventLogFn: this.logComponentEvent.bind(this),
			withPrivateConstants: true
		});

		this.dCtx = new DesignContext({
			resolvers: {
				actionRef: runtimeResolvers.actionRef,
				function: runtimeResolvers.function,
				translate: runtimeResolvers.translate,
				viewRef: runtimeResolvers.viewRef,
				...integrationResolvers
			},
			readMode: DESIGN_CONTEXT_READ_MODE.FULL,
			enableCompletition: true
		});

		this.rCtx = this.renderCtx.getRuntimeContext();

		onEvent(this.dCtx.modelChangeEvent, () => {
			this.createSnapshot();
			this.handleModelChange();
		});

		onEvent(this.rCtx.renderEvent, (spec) => {
			this.modelSpec = spec;
			this.buildParamsModel();
			emitEvent(this.onRender);
		});

		onEvent(
			this.renderCtx.getGlobalsContainer().onChange,
			(this.handleGlobalsContainerChange = () => {
				this.updateScope(true);
			})
		);

		onEvent(this.rCtx.errorEvent, () => {
			emitEvent(this.onRuntimeErrors);
		});
	}

	private async initModel(): Promise<void> {
		this.createSnapshot();
		this.updateScope(false);
		this.handleModelChange();
		this.buildParamsModel();
	}

	/**
	 * Creates an empty action blueprint
	 */
	public async createDefault(): Promise<void> {
		this.model = BlueprintEndpoint.createDefault(this.dCtx, null);

		await this.dCtx.waitForPendingOperations();
		await this.initModel();
	}

	/**
	 * Loads document
	 *
	 * @param blueprintContent Blueprint YAML contents
	 */
	public async loadDocument(blueprintContent: string): Promise<void> {
		this.dCtx.clearAllParseErrors();

		this.model = this.dCtx.loadYAMLDocument(BlueprintEndpoint, blueprintContent, "local://endpoint");
		await this.dCtx.waitForPendingOperations();

		emitEvent(this.onParseErrors);

		await this.initModel();

		// eslint-disable-next-line max-len
		const nodesListValueModel = this.model.props.spec.constant.props.nodes
			.value as unknown as ISchemaFlowNodeListModel<ISchemaFlowNodeTypeDefinitionMap>;
		this.selection.setFlowNodeSelection(nodesListValueModel, [ nodesListValueModel.items[0] ]);
	}

	/**
	 * Returns document as YAML
	 */
	public exportDocumentAsYAML(): string {
		if (!this.model) {
			throw new Error("Model is not initialized.");
		}

		const idt = this.model.schema.serialize(this.model, [ "$" ]);
		return serializeIDTToYAML(idt);
	}

	/**
	 * Compiles model to native code
	 */
	public compile(): ICompileResult {
		if (!this.model) {
			throw new Error("Model is not initialized.");
		}

		const cCtx = new CompileContext({
			resolvers: this.renderCtx.getResolvers()
		});

		return cCtx.compileModel(this.model);
	}

	private logComponentEvent(eventType, opts, metaData, result) {
		if (window.APP_DEBUG) {
			// eslint-disable-next-line max-len
			console.groupCollapsed(
				"[EventSystem] Handled event '%s' #%d node ID '%s' (%s)",
				metaData.eventName,
				metaData.executionOrder,
				metaData.nodeId,
				eventType
			);
			console.log("Options:", opts);
			console.log("Result:", result);
			console.log("Sender:", {
				id: metaData.senderId,
				path: metaData.senderPath,
				modelNodeId: metaData.senderModelNodeId,
				instance: metaData.senderInstance
			});
			console.log("Input Scope:", metaData.inputScope);
			console.groupEnd();
		}
	}

	/**
	 * Returns last rendered spec (or null)
	 */
	public getLastSpec(): TGetBlueprintSchemaSpec<TBlueprintViewSchema> {
		return this.modelSpec;
	}

	private updateScope(triggerRender: boolean) {
		// @todo add view params
		const scope = createViewScope(this.rCtx, {});
		this.rCtx.setScope(scope, triggerRender);
	}

	public getMode(): ENDPOINT_MODEL_MODE {
		const renderCtxMode = this.renderCtx.mode;
		const runtimeCtxMode = this.rCtx.getMode();

		if (renderCtxMode === RUNTIME_CONTEXT_MODE.EDITOR) {
			if (runtimeCtxMode === RUNTIME_CONTEXT_MODE.EDITOR) {
				return ENDPOINT_MODEL_MODE.EDIT;
			}

			return ENDPOINT_MODEL_MODE.PREVIEW_EDIT;
		}

		return ENDPOINT_MODEL_MODE.PREVIEW;
	}

	public setMode(mode: ENDPOINT_MODEL_MODE): void {
		let renderCtxMode: RUNTIME_CONTEXT_MODE;
		let runtimeCtxMode: RUNTIME_CONTEXT_MODE;

		switch (mode) {
			case this.getMode():
				return;
			case ENDPOINT_MODEL_MODE.PREVIEW:
				renderCtxMode = RUNTIME_CONTEXT_MODE.NORMAL;
				runtimeCtxMode = RUNTIME_CONTEXT_MODE.NORMAL;
				break;
			case ENDPOINT_MODEL_MODE.EDIT:
				renderCtxMode = RUNTIME_CONTEXT_MODE.EDITOR;
				runtimeCtxMode = RUNTIME_CONTEXT_MODE.EDITOR;
				break;
			default:
				return;
		}

		this.renderCtx.setMode(renderCtxMode);
		this.rCtx.setMode(runtimeCtxMode, true); // trigger must be true otherwise react part will be desynchronized from spec
		this.renderModel();
	}

	private renderModel() {
		if (this.renderRequested) {
			return;
		}

		this.renderRequested = true;

		setTimeout(() => {
			this.renderRequested = false;

			this.rCtx.clearAllRuntimeErrors();
			this.rCtx.render();
		}, 1);
	}

	/**
	 * Returns design context
	 */
	public getDesignContext(): DesignContext {
		return this.dCtx;
	}

	/**
	 * Returns runtime context
	 */
	public getRuntimeContext(): RuntimeContext<TBlueprintEndpointSchema> {
		return this.rCtx;
	}

	/**
	 * Returns blueprint model
	 */
	public getModel(): TGetBlueprintSchemaModel<TBlueprintEndpointSchema> {
		return this.model;
	}

	/**
	 * Returns editor selection instance
	 */
	public getSelection(): EndpointEditorEditSelection {
		return this.selection;
	}

	/**
	 * Returns all errors in a format for error console
	 */
	public getErrors(): IErrorListGroup {
		return {
			documentUri: null,
			parseErrors: this.dCtx.getParseDocumentErrors("local://endpoint"),
			runtimeErrors: this.rCtx.getRuntimeErrors(true),
			compileErrors: []
		};
	}

	private undoRedoHandle(snapshot) {
		this.model = BlueprintEndpoint.parse(this.dCtx, snapshot);
		this.updateScope(true);
		this.renderModel();
		this.lastSnapshot = snapshot;
		this.selection.deselectAll();

		emitEvent(this.onModelChange);
		emitEvent(this.onParamsModelChange);
		emitEvent(this.onRender);
	}

	private createSnapshot = debounce(() => {
		const prevSnapshot = this.lastSnapshot;
		const currentSnapshot = this.model.schema.serialize(this.model, [ "$" ]);
		this.lastSnapshot = currentSnapshot;

		if (prevSnapshot) {
			this.undoRedoManager.add({
				undo: () => this.undoRedoHandle(prevSnapshot),
				redo: () => this.undoRedoHandle(currentSnapshot),
				disposeUndo: () => {
					/** */
				},
				disposeRedo: () => {
					/** */
				}
			});
			emitEvent(this.onRender);
		}
	}, 500);

	private handleModelChange() {
		// Check selection
		this.checkSelection();

		emitEvent(this.onModelChange);
	}

	private checkSelection() {
		const items = this.selection.getItems().slice();

		for (let i = 0; i < items.length; i++) {
			if (!this.dCtx.getNodeById(items[i].nodeId)) {
				this.selection.deselectByNodeId(items[i].nodeId);
			}
		}
	}

	/**
	 * Assigns action debug data to flow nodes
	 */
	public assignDebugData(debugData: IActionDebugData): void {
		if (!this.model) {
			return;
		}

		const flowNodeList = this.model.props.spec.constant.props.nodes.value;

		flowNodeList.items.forEach((node) => {
			const nodeDebugData = debugData.nodes[node.id];

			if (nodeDebugData) {
				node.schema.setExecutionResult(
					node,
					{
						scope: nodeDebugData.inputScope,
						resolvedOpts: nodeDebugData.nodeOpts as any,
						activeOutput: nodeDebugData.output.name,
						outputData: nodeDebugData.output.data,
						outputType: nodeDebugData.output.type,
						debug: nodeDebugData.debug,
						executionTimeInMs: nodeDebugData.executionTimeInMs
					},
					true
				);
			}
		});

		emitEvent(flowNodeList.changeEvent);
	}

	public buildParamsModel(): void {
		const dCtx = this.dCtx;

		if (this.paramsModel) {
			const prevDataIdt = this.paramsModel.schema.serialize(this.paramsModel, [ "$" ]);
			this.paramsModel.schema.destroy(this.paramsModel);

			this.paramsModel = paramsSchema.parse(dCtx, prevDataIdt);
		} else {
			this.paramsModel = paramsSchema.createDefault(dCtx, null);
		}

		emitEvent(this.onParamsModelBuilt);
		emitEvent(this.onParamsModelChange);
	}

	public getParamsModel(): ISchemaConstObjectModel<TSchemaConstObjectProps> {
		return this.paramsModel;
	}

	public clearAllParseErrors(): void {
		this.dCtx.clearAllParseErrors();
		emitEvent(this.onParseErrors);
	}

	/**
	 * Exports values from params model (eg. test final params)
	 * Returns null if model is not available
	 */
	public async exportParamsData(): Promise<TGetBlueprintSchemaSpec<typeof paramsSchema>> {
		if (!this.paramsModel) {
			return null;
		}

		const rCtx = new RuntimeContext(
			{
				mode: RUNTIME_CONTEXT_MODE.NORMAL,
				inEditor: true,
				resolvers: {}
			},
			createRenderFunctionFromModel(this.paramsModel),
			createEmptyScope()
		);

		await rCtx.renderAsync();

		const paramsData = rCtx.getLastSpec();

		rCtx.destroy();

		return paramsData;
	}

	/**
	 * Clears debug data from nodes
	 */
	public clearDebugData(): void {
		if (!this.model) {
			return;
		}

		const flowNodeList = this.model.props.spec.constant.props.nodes.value;

		flowNodeList.items.forEach((node) => {
			node.schema.clearExecutionResult(node, true);
		});

		emitEvent(flowNodeList.changeEvent);
	}

	public dispose(): void {
		this.routingManager.dispose();

		if (this.model) {
			this.model.schema.destroy(this.model);
		}

		this.dCtx.destroy();
		this.selection.destroy();

		removeAllEventListeners(this.onModelChange);
		removeAllEventListeners(this.onRuntimeErrors);
		removeAllEventListeners(this.onParseErrors);

		offEvent(this.renderCtx.getGlobalsContainer().onChange, this.handleGlobalsContainerChange);
	}
}
